import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Loader2, Play, History, Pencil, Trash2, Radar, AlertOctagon,
  ShieldCheck, CheckCircle2, XCircle, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  SEV_STYLE, STATUS_STYLE, STATUS_LABEL, INCIDENT_STATUS_STYLE, PURPOSE_LABEL,
  OPERATOR_LABEL, DIRECTION_LABEL, labelize, fmtNum, timeAgo,
} from '@/lib/sentinelConstants';
import ProcessHelp from '@/components/ProcessHelp';

const DETECTION_SUMMARY = {
  static_threshold: (p) => `${OPERATOR_LABEL[p.operator] || p.operator} ${fmtNum(p.value)}`,
  dynamic_threshold: (p) => `${p.band_sigma}σ band over ${p.baseline_window} periods`,
  rate_of_change: (p) => `> ${p.max_pct}% per period (${DIRECTION_LABEL[p.direction] || p.direction})`,
  sustained_trend: (p) => `${p.min_periods} periods ${DIRECTION_LABEL[p.direction] || p.direction}`,
  seasonal_anomaly: (p) => `${p.z}σ from ${p.season ? `season ${p.season}` : 'its history'}`,
  forecast_threshold: (p) => `projected ${OPERATOR_LABEL[p.operator] || p.operator} ${fmtNum(p.value)} within ${p.horizon}`,
  forecast_variance: (p) => `projection > ${p.max_pct}% from last actual`,
  data_quality: (p) => `stale after ${p.stale_days} days / open DQ incidents`,
};

function ReplayChart({ replay }) {
  const pts = replay?.points || [];
  if (pts.length < 2) return <div className="text-xs text-muted-foreground">Not enough history to replay.</div>;
  const vals = pts.map((p) => Number(p.value) || 0);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const W = 640, H = 120, pad = 8;
  const step = (W - pad * 2) / (pts.length - 1);
  const coords = pts.map((p, i) => [pad + i * step, H - pad - (((Number(p.value) || min) - min) / range) * (H - pad * 2)]);
  const d = coords.map((c, i) => `${i ? 'L' : 'M'}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' ');
  const sevColor = (s) => (s === 'critical' ? '#e11d48' : s === 'high' ? '#f97316' : s === 'medium' ? '#f59e0b' : '#94a3b8');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" data-testid="sentinel-replay-chart">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground/50" />
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r={pts[i].fired ? 4 : 2.2}
          fill={pts[i].fired ? sevColor(pts[i].severity) : 'currentColor'}
          className={pts[i].fired ? '' : 'text-muted-foreground/40'}>
          <title>{`${pts[i].period} · ${fmtNum(pts[i].value)}${pts[i].fired ? ` · would fire (${pts[i].severity})` : ''}`}</title>
        </circle>
      ))}
    </svg>
  );
}

export default function SentinelDetailPage() {
  const { sentinelId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [replay, setReplay] = useState(null);
  const [transitions, setTransitions] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, opts] = await Promise.all([api.getSentinel(sentinelId), api.getSentinelOptions()]);
      setData(res);
      setTransitions(opts.transitions || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load Sentinel');
    } finally {
      setLoading(false);
    }
  }, [sentinelId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  const s = data.sentinel;
  const runs = data.runs || [];
  const incidents = data.incidents || [];
  const allowedTransitions = transitions[s.status] || [];

  const evaluate = async () => {
    setBusy('evaluate');
    try {
      const r = await api.evaluateSentinel(sentinelId);
      if (r.fired && r.material) toast.warning(`Fired — ${r.run?.severity} severity`);
      else if (r.fired) toast.info('Detected a change below the materiality bar');
      else toast.success('All detection methods within limits');
      load();
    } catch (e) { toast.error(e.message || 'Evaluation failed'); } finally { setBusy(''); }
  };
  const doReplay = async () => {
    setBusy('replay');
    try { setReplay(await api.replaySentinel(sentinelId)); }
    catch (e) { toast.error(e.message || 'Replay failed'); } finally { setBusy(''); }
  };
  const transition = async (to) => {
    setBusy(`t_${to}`);
    try { await api.transitionSentinel(sentinelId, to); toast.success(`Sentinel is now ${STATUS_LABEL[to] || to}`); load(); }
    catch (e) { toast.error(e.message || 'Transition failed'); } finally { setBusy(''); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this Sentinel? This cannot be undone.')) return;
    setBusy('delete');
    try { await api.deleteSentinel(sentinelId); toast.success('Sentinel deleted'); navigate('/admin/sentinels'); }
    catch (e) { toast.error(e.message || 'Delete failed'); setBusy(''); }
  };
  const saveAsPack = async () => {
    const name = window.prompt('Name this pack — a reusable template of this Sentinel’s tuned config:', `${s.name} pack`);
    if (name === null) return;
    setBusy('pack');
    try {
      await api.saveSentinelAsPack(sentinelId, (name || '').trim());
      toast.success('Saved as a reusable pack — stamp it onto other metrics via “New from pack”');
    } catch (e) { toast.error(e.message || 'Could not save pack'); } finally { setBusy(''); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="sentinel-detail-page">
      <button className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        onClick={() => navigate('/admin/sentinels')} data-testid="sentinel-back">
        <ArrowLeft className="w-4 h-4" /> All Sentinels
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Radar className="w-6 h-6 text-primary" /> {s.name}
          </h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={STATUS_STYLE[s.status]}>{STATUS_LABEL[s.status] || s.status}</Badge>
            <Badge variant="outline">{PURPOSE_LABEL[s.purpose] || s.purpose}</Badge>
            <span className="text-xs text-muted-foreground">v{s.version} · {s.model_name} · {s.metric_label || s.metric}</span>
          </div>
          {s.description ? <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{s.description}</p> : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ProcessHelp
            testId="sentinel-detail-help"
            title="Sentinel Detail & Save as Pack"
            section="sentinel-packs"
            intro="Review a Sentinel's definition and incidents, run it on demand, and turn a tuned Sentinel into a reusable pack."
            steps={[
              'Use the Lifecycle controls to move it between draft, active and retired.',
              'Evaluate now runs it against the latest data; Replay history re-runs it over past periods.',
              'Save as pack captures this tuned configuration (thresholds, materiality, recipients) as a reusable template.',
              'Stamp that pack onto other metrics from the Sentinels list ("New from pack") or the Pack Library.',
              'Open any incident below to acknowledge, assign, resolve and verify recovery in the Incidents inbox.',
            ]}
          />
          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/sentinels/${sentinelId}/edit`)} data-testid="sentinel-edit">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit
          </Button>
          <Button size="sm" variant="outline" disabled={busy === 'pack'} onClick={saveAsPack} data-testid="sentinel-save-as-pack">
            {busy === 'pack' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Package className="w-4 h-4 mr-1.5" /> Save as pack</>}
          </Button>
          <Button size="sm" variant="outline" disabled={busy === 'evaluate'} onClick={evaluate} data-testid="sentinel-detail-evaluate">
            {busy === 'evaluate' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" /> Evaluate now</>}
          </Button>
          <Button size="sm" variant="outline" disabled={busy === 'replay'} onClick={doReplay} data-testid="sentinel-replay">
            {busy === 'replay' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><History className="w-4 h-4 mr-1.5" /> Replay history</>}
          </Button>
        </div>
      </div>

      {/* Lifecycle */}
      <div className="rounded-xl border border-border/60 bg-card/60 p-4">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Lifecycle</div>
        <div className="flex items-center gap-2 flex-wrap">
          {allowedTransitions.length === 0 ? <span className="text-sm text-muted-foreground">No transitions available.</span> : null}
          {allowedTransitions.map((to) => (
            <Button key={to} size="sm" variant={to === 'active' ? 'default' : 'outline'} disabled={!!busy}
              onClick={() => transition(to)} data-testid={`sentinel-transition-${to}`}>
              {busy === `t_${to}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `→ ${STATUS_LABEL[to] || to}`}
            </Button>
          ))}
          {(s.status === 'draft' || s.status === 'retired') ? (
            <Button size="sm" variant="ghost" className="text-rose-500" disabled={!!busy} onClick={remove} data-testid="sentinel-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Definition */}
        <div className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Detection methods ({s.combine.toUpperCase()})</div>
          <div className="space-y-2">
            {(s.detection || []).map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <Badge variant="outline" className="shrink-0">{labelize(d.type)}</Badge>
                <span className="text-muted-foreground">{(DETECTION_SUMMARY[d.type] || (() => ''))(d.params || {})}</span>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-border/40 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground text-xs">Materiality</span><br />
              {s.materiality?.kind === 'percent' ? `≥ ${s.materiality.min_pct}%`
                : s.materiality?.kind === 'absolute' ? `≥ ${fmtNum(s.materiality.min_abs)}`
                : `≥ ${fmtNum(s.materiality.min_financial)} value`}</div>
            <div><span className="text-muted-foreground text-xs">Base severity</span><br /><Badge className={SEV_STYLE[s.base_severity]}>{s.base_severity}</Badge></div>
            <div><span className="text-muted-foreground text-xs">Persistence</span><br />{s.persistence} eval(s)</div>
            <div><span className="text-muted-foreground text-xs">Cooldown</span><br />{s.cooldown_hours}h</div>
            <div><span className="text-muted-foreground text-xs">Grain</span><br />{s.grain}</div>
            <div><span className="text-muted-foreground text-xs">Lookback</span><br />{s.lookback_periods} periods</div>
          </div>
          <div className="pt-2 border-t border-border/40 text-xs text-muted-foreground">
            Notify: {(s.notify?.roles || []).join(', ')}{s.notify?.owner ? ' + owner' : ''}
            {(s.notify?.channels || []).length ? ` · ${s.notify.channels.join(', ')}` : ''}
          </div>
        </div>

        {/* Replay + recent runs */}
        <div className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Historical replay</div>
          {replay ? (
            <>
              <ReplayChart replay={replay} />
              <div className="text-xs text-muted-foreground">
                Would have fired at {replay.fired_count} of {replay.points.length} historical points.
                {replay.note ? ` ${replay.note}` : ''}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center">
              Run “Replay history” to see how this Sentinel would have behaved over the past.
            </div>
          )}
        </div>
      </div>

      {/* Recent runs */}
      <div className="rounded-xl border border-border/60 bg-card/60 p-4">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Recent evaluations</div>
        {runs.length === 0 ? <div className="text-sm text-muted-foreground">No evaluations yet — run “Evaluate now”.</div> : (
          <div className="space-y-1.5">
            {runs.map((r) => (
              <div key={r.run_id} className="flex items-center gap-3 text-sm py-1.5 border-b border-border/40 last:border-0" data-testid={`sentinel-run-${r.run_id}`}>
                {r.fired && r.material ? <XCircle className="w-4 h-4 text-rose-500 shrink-0" />
                  : r.fired ? <AlertOctagon className="w-4 h-4 text-amber-500 shrink-0" />
                  : <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                <span className="flex-1 line-clamp-1">{r.headline}</span>
                {r.severity ? <Badge className={SEV_STYLE[r.severity]}>{r.severity}</Badge> : null}
                <span className="text-xs text-muted-foreground shrink-0">{timeAgo(r.generated_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linked incidents */}
      <div className="rounded-xl border border-border/60 bg-card/60 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Incidents</div>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/incidents?sentinel_id=${sentinelId}`)}>Open inbox</Button>
        </div>
        {incidents.length === 0 ? <div className="text-sm text-muted-foreground">No incidents raised by this Sentinel.</div> : (
          <div className="space-y-1.5">
            {incidents.map((i) => (
              <div key={i.incident_id} className="flex items-center gap-3 text-sm py-1.5 border-b border-border/40 last:border-0">
                <Badge className={SEV_STYLE[i.severity]}>{i.severity}</Badge>
                <span className="flex-1 line-clamp-1">{i.headline}</span>
                <Badge className={INCIDENT_STATUS_STYLE[i.status]}>{i.status}</Badge>
                <span className="text-xs text-muted-foreground shrink-0">{timeAgo(i.opened_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
